import { useNavigation } from "@react-navigation/native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { LayoutNodeInput } from "@towersystems/roam-common/lib/generated-types";
import { useCallback, useState } from "react";
import { RegisterRootParamList } from "../../../../types";
import { DragDropActionTypes } from "../../../../utilities/drag-drop";
import { useFlashMessages } from "@towersystems/roam-common-native";
import {
  PageItemType,
  quickKeyHasForm,
  useLayoutEditController,
} from "../../../../utilities/layout";
import { LayoutScreens } from "../../../../utilities/layout/constants";
import { EditTileContext } from "../../../../utilities/layout/context";
import { configToEditNavigationArgs } from "./utils";

export type EditTileProviderProps = Partial<
  NativeStackScreenProps<RegisterRootParamList, "EditQuickKeyRootScreen">
> & {
  children?: React.ReactNode;
};

export function EditTileProvider({ children }: EditTileProviderProps) {
  const navigation = useNavigation<EditTileProviderProps["navigation"]>();
  const { showMessage } = useFlashMessages();
  const performLayoutAction = useLayoutEditController();

  const [item, setItem] = useState<PageItemType | null>(null);

  const openEditQuickKey = useCallback(
    (itemToEdit: PageItemType) => {
      if (!itemToEdit.config) return;

      setItem(itemToEdit);

      if (quickKeyHasForm(itemToEdit.config)) {
        const options = configToEditNavigationArgs(itemToEdit.config);
        navigation?.navigate(
          LayoutScreens.EditQuickKeyRootScreen,
          options as any
        );
      } else {
        navigation?.navigate(LayoutScreens.EditQuickKeyRootScreen);
      }
    },
    [navigation?.navigate]
  );

  async function handleUpdateTile(formValues: LayoutNodeInput) {
    return performLayoutAction({
      type: DragDropActionTypes.UPDATE_POS_LAYOUT_KEY,
      payload: formValues,
    })
      .then(() => {
        navigation?.navigate("SellScreen");
        showMessage({
          message: "Successfully Updated Tile",
          severity: "success",
        });
      })
      .catch((e) => {
        showMessage({ message: e?.toString(), severity: "error" });
      });
  }

  async function handleRemoveTile(id: string) {
    return performLayoutAction({
      type: DragDropActionTypes.DELETE_POS_LAYOUT_KEY,
      payload: {
        id,
      },
    })
      .then(() => {
        navigation?.navigate("SellScreen");
        showMessage({
          message: "Successfully Removed Tile",
          severity: "success",
        });
      })
      .catch((e) => {
        showMessage({ message: e?.toString(), severity: "error" });
      });
  }

  return (
    <EditTileContext.Provider
      value={{
        item,
        openEditTile: openEditQuickKey,
        handleUpdateTile,
        handleRemoveTile,
      }}
    >
      {children}
    </EditTileContext.Provider>
  );
}
